import React from 'react';
import Header from '../Home/sections/Header';
import FooterSection from '../Home/sections/FooterSection';
import SEO from '../../components/common/SEO';
import {faqSchema} from '../../utils/structuredData';

const HelpCenter: React.FC = () => {
  const supportEmail = 'karan@cadencepro.ai';

  const faqs = [
    {
      question: "How do Cadencepro's Entities relate to Azure Entities?",
      answer: "In CadencePro we have 3 entities: Strategy Maps map to Azure ADO Epics, Work Units map to Azure ADO Features, and Tasks map to Azure ADO User Stories/Tasks."
    },
    {
      question: "How are work items bubbled up in Cadencepro for completion percentage?",
      answer: "Cadencepro evaluates User Stories/Tasks by Area Path and Board Columns. Feature % Complete is a weighted average bubble-up of underlying User Stories/Tasks. Epic % Complete is a simple average bubble-up of all Features underneath. Work items in the 'Removed' state are excluded from these calculations."
    },
    {
      question: "How does Cadencepro determine if a task is delayed?",
      answer: "Cadencepro uses the Iteration Path to determine if a sprint is past, present, or future. Items are not marked delayed until the sprint is complete. If a sprint is in the past, any incomplete User Stories are marked delayed. Until a sprint is current, items are not shown as delayed."
    },
    {
      question: "How to update the percentage mapping against the project board in Cadence?",
      answer: "After completing Azure ADO integration, Cadencepro imports boards across projects for all teams. You can map each board column to a percentage value. To update: Strategy Map / Work Unit page → Import Projects (top right) → Project States. Updates apply automatically in a nightly job, or you can manually sync an Epic for instant changes."
    },
    {
      question: "How does percentage complete calculation work?",
      answer: "Cadencepro evaluates the Team board and uses the state-to-% complete mapping to compute progress. The mapping is defined by the client (admin can set % values per column). User Story completion bubbles up to Feature completion via weighted average of User Story day estimates."
    },
    {
      question: "How does Cadencepro bring in EPICs and keep them updated?",
      answer: "We use a 3-way approach: 1) Select one or more Epics and click Import. 2) A webhook tracks changes and updates Epics and related child tickets automatically. 3) If needed, users can resync the entire Epic on demand."
    },
    {
      question: "How does Cadencepro decide the duration of a User Story or Task?",
      answer: "Cadencepro uses Story Points to compute 8-hour days for planning and tracking."
    },
    {
      question: "How is the User Story/Task due date calculated?",
      answer: "When importing, Cadencepro uses the Activated Start Date (if present) and adds the calculated duration to determine the Due Date."
    },
    {
      question: "How does Cadencepro bring in teams and resources?",
      answer: "Under 'Manage People', users can import Teams and Resources directly. Cadencepro maps imported teams and corresponding resources automatically, creating the required structures in the platform."
    },
    {
      question: "If a closed Epic is reopened, does it show At Risk / On Track?",
      answer: "Yes. The Epic will show the correct status after reopening."
    },
    {
      question: "If an Epic or Feature is closed in Azure, will it be closed in Cadencepro?",
      answer: "Yes. It will be marked Closed in Cadencepro regardless of % complete."
    },
    {
      question: "How does 'Removed' impact % computation?",
      answer: "Work items marked 'Removed' are excluded from percentage computation."
    }
  ];

  const faqStructuredData = faqSchema(faqs);

  return (
    <div className="min-h-screen w-full bg-black">
      <SEO
        title="Help Center - CadencePro.ai | FAQs & Support Documentation"
        description="Find answers to common questions about CadencePro. Learn about Azure integration, work item tracking, percentage calculations, and more. Contact support for additional help."
        canonical="/help-center"
        keywords="cadencepro help, project management faq, azure devops integration, cadencepro support, how to use cadencepro"
        structuredData={faqStructuredData}
      />
      <div className="pointer-events-none absolute -top-6 left-2 w-72 h-48 opacity-40 bg-gradient-to-tr from-blue-700 via-indigo-800 to-blue-700 rounded-2xl blur-3xl" />
      <div className="pointer-events-none absolute bottom-[1050px] right-0 w-72 h-48 opacity-30 bg-gradient-to-tr from-blue-700 via-indigo-800 to-blue-700 rounded-2xl blur-3xl" />
      <div className="pointer-events-none absolute top-16 right-6 w-56 h-40 opacity-25 bg-gradient-to-tr from-blue-700 via-indigo-800 to-blue-700 rounded-2xl blur-3xl" />
      <div className="pointer-events-none absolute top-[780px] right-6 w-56 h-40 opacity-25 bg-gradient-to-tr from-blue-700 via-indigo-800 to-blue-700 rounded-2xl blur-3xl" />
      <div className="pointer-events-none absolute top-1/2 -translate-y-1/2 left-6 w-64 h-44 opacity-25 bg-gradient-to-tr from-blue-700 via-indigo-800 to-blue-700 rounded-2xl blur-3xl" />
      <div className="pointer-events-none absolute bottom-44 left-1/4 w-72 h-52 opacity-20 bg-gradient-to-tr from-blue-700 via-indigo-800 to-blue-700 rounded-2xl blur-3xl" />

      <Header onlyLogo={false} />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <header className="mb-8">
          <h1 className="text-2xl sm:text-3xl font-semibold text-white">
            Help Center
          </h1>
          <p className="mt-3 text-sm sm:text-base text-gray-300">
            Find answers to common questions and learn how to work with Cadencepro. If you can’t find
            what you’re looking for here, or you need additional assistance, contact us at{' '}
            <a
              className="text-blue-400 underline"
              href={`mailto:${supportEmail}`}
            >
              {supportEmail}
            </a>.
          </p>
        </header>

        <section className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-10">
          <div className="rounded-xl border border-slate-700 bg-slate-900/80 p-4">
            <h2 className="text-base font-medium text-white">
              Can’t sign in?
            </h2>
            <p className="mt-2 text-sm text-gray-300">
              If you’re unable to log in, please first{' '}
              <a className="text-blue-400 underline" href="/book-a-demo">
                book a demo
              </a>{' '}
              so our team can set up your account properly and walk you through access. If you’ve
              already done that and are still facing issues after trying a password reset, reach out at{' '}
              <a
                className="text-blue-400 underline"
                href={`mailto:${supportEmail}`}
              >
                {supportEmail}
              </a>.
            </p>
          </div>
          <div className="rounded-xl border border-slate-700 bg-slate-900/80 p-4">
            <h2 className="text-base font-medium text-white">
              Troubleshooting integrations
            </h2>
            <p className="mt-2 text-sm text-gray-300">
              For integration setup, permissions, or data sync issues, review the FAQs below. If an
              answer isn’t there, email{' '}
              <a
                className="text-blue-400 underline"
                href={`mailto:${supportEmail}`}
              >
                {supportEmail}
              </a>{' '}
              and we’ll help.
            </p>
          </div>
        </section>

        {/*<section className="mb-10">*/}
        {/*  <h2 className="text-xl font-semibold text-white mb-3">*/}
        {/*    Purpose*/}
        {/*  </h2>*/}
        {/*  <p className="text-sm text-gray-300">*/}
        {/*    The purpose of this page is to make sure that important logic is written in single place for*/}
        {/*    implementation partners to understand and align.*/}
        {/*  </p>*/}
        {/*</section>*/}

        <section className="mb-10">
          <h2 className="text-xl font-semibold text-white mb-4">
            Frequently Asked Questions
          </h2>

          <div className="space-y-6">
            <div className="rounded-xl border border-slate-700 bg-slate-900/80 p-5">
              <h3 className="text-base font-semibold text-white">
                How do Cadencepro’s Entities relate to Azure Entities?
              </h3>
              <p className="mt-3 text-sm text-gray-300">
                In CadencePro we have 3 entities -
              </p>
              <ul className="mt-3 list-disc pl-5 text-sm text-gray-300 space-y-1">
                <li>Strategy Maps → Azure ADO Epics</li>
                <li>Work Units → Azure ADO Features</li>
                <li>Tasks → Azure ADO User Stories / Tasks</li>
              </ul>
            </div>

            <div className="rounded-xl border border-slate-700 bg-slate-900/80 p-5">
              <h3 className="text-base font-semibold text-white">
                How are work items bubbled up in Cadencepro for completion %?
              </h3>
              <ul className="mt-3 list-disc pl-5 text-sm text-gray-300 space-y-1">
                <li>Cadencepro evaluates User Stories/Tasks by Area Path and Board Columns and maps them to a completion %.</li>
                <li>Feature % Complete = weighted average bubble-up of underlying User Stories/Tasks.</li>
                <li>Epic % Complete = simple average bubble-up of all Features underneath.</li>
              </ul>
              <p className="mt-3 text-sm text-gray-400">
                Notes:
                <br />1) Board-column-to-% mapping can be set up in Cadencepro.
                <br />2) Work items in the “Removed” state are excluded from these calculations.
              </p>
            </div>

            <div className="rounded-xl border border-slate-700 bg-slate-900/80 p-5">
              <h3 className="text-base font-semibold text-white">
                How does Cadencepro determine if a task/user story is delayed with respect to current, past, or future sprints?
              </h3>
              <ul className="mt-3 list-decimal pl-5 text-sm text-gray-300 space-y-1">
                <li>Get the Iteration Path of the User Story/Task.</li>
                <li>From the Iteration Path, determine if the sprint is past, present, future, or unassigned.</li>
                <li>If an Iteration Path (sprint) is present, items are not marked delayed until the sprint is complete.</li>
                <li>If a 2-day item in a current sprint is less than 50% complete on the last day of the sprint, it will show as delayed. If the user is not allocated, it will show as unallocated.</li>
                <li>If the sprint is in the past, any incomplete User Stories are marked delayed.</li>
                <li>If the sprint is in the future and not assigned, items are marked Not Started and never delayed.</li>
              </ul>
              <p className="mt-3 text-sm text-gray-400">
                Note: Until a sprint is current, we do not show User Stories/Tasks as delayed, as planned items may not have started yet.
              </p>
            </div>

            <div className="rounded-xl border border-slate-700 bg-slate-900/80 p-5">
              <h3 className="text-base font-semibold text-white">
                How to update the percentage mapping against the project board in Cadence?
              </h3>
              <p className="mt-3 text-sm text-gray-300">
                After completing Azure ADO integration, Cadencepro imports boards across projects for all teams.
                You can map each board column to a percentage value used to update task completion.
              </p>
              <p className="mt-3 text-sm text-gray-300">
                To update: Strategy Map / Work Unit page → Import Projects (top right) → Project States
              </p>
              <p className="mt-3 text-sm text-gray-400">
                Note: Updating the percentages will apply automatically in a nightly job. You can also manually sync an Epic to see changes instantly.
              </p>
            </div>

            <div className="rounded-xl border border-slate-700 bg-slate-900/80 p-5">
              <h3 className="text-base font-semibold text-white">
                How does percentage complete calculation work?
              </h3>
              <ul className="mt-3 list-disc pl-5 text-sm text-gray-300 space-y-1">
                <li>Cadencepro evaluates the Team board and uses the state-to-%complete mapping to compute progress.</li>
                <li>The mapping is defined by the client (admin can set % values per column).</li>
                <li>User Story completion bubbles up to Feature completion via weighted average of User Story day estimates.</li>
              </ul>
              <p className="mt-3 text-sm text-gray-400">
                Example: If one User Story is Not Started and another is Ready for QA at 50%, overall Feature status might be 25% (assuming equal durations).
              </p>
            </div>

            <div className="rounded-xl border border-slate-700 bg-slate-900/80 p-5">
              <h3 className="text-base font-semibold text-white">
                How does Cadencepro bring in EPICs and keep them updated? How are conflicts resolved?
              </h3>
              <p className="mt-3 text-sm text-gray-300">
                We use a 3 way approach for importing an EPIC and corresponding Features and user stories.
              </p>
              <ul className="mt-3 list-decimal pl-5 text-sm text-gray-300 space-y-1">
                <li>Select one or more Epics and click Import.</li>
                <li>A webhook tracks changes and updates Epics and related child tickets automatically.</li>
                <li>If needed, users can resync the entire Epic on demand.</li>
              </ul>
            </div>

            <div className="rounded-xl border border-slate-700 bg-slate-900/80 p-5">
              <h3 className="text-base font-semibold text-white">
                How does Cadencepro decide the duration of a User Story or Task?
              </h3>
              <p className="mt-3 text-sm text-gray-300">
                Cadencepro uses Story Points to compute 8‑hour days for planning and tracking.
              </p>
            </div>

            <div className="rounded-xl border border-slate-700 bg-slate-900/80 p-5">
              <h3 className="text-base font-semibold text-white">
                How is the User Story/Task due date calculated?
              </h3>
              <p className="mt-3 text-sm text-gray-300">
                When importing, Cadencepro uses the Activated Start Date (if present) and adds the calculated duration to determine the Due Date.
              </p>
            </div>

            <div className="rounded-xl border border-slate-700 bg-slate-900/80 p-5">
              <h3 className="text-base font-semibold text-white">
                How does Cadencepro bring in teams and resources and keep them updated?
              </h3>
              <p className="mt-3 text-sm text-gray-300">
                Under "Manage People", users can import Teams and Resources directly. Cadencepro maps imported teams
                and corresponding resources automatically, creating the required structures in the platform.
              </p>
              <p className="mt-3 text-sm text-gray-400">
                In future, when new users are added to an imported team, you’ll see a visual cue on the Import Teams
                screen to import updates. This functionality is under active development and may change.
              </p>
            </div>

            <div className="rounded-xl border border-slate-700 bg-slate-900/80 p-5">
              <h3 className="text-base font-semibold text-white">
                If a closed Epic is reopened, does it show At Risk / On Track?
              </h3>
              <p className="mt-3 text-sm text-gray-300">
                Yes. The Epic will show the correct status after reopening.
              </p>
            </div>

            <div className="rounded-xl border border-slate-700 bg-slate-900/80 p-5">
              <h3 className="text-base font-semibold text-white">
                If an Epic or Feature is closed in Azure, will it also be closed in Cadencepro?
              </h3>
              <p className="mt-3 text-sm text-gray-300">
                Yes. It will be marked Closed in Cadencepro regardless of % complete.
              </p>
            </div>

            <div className="rounded-xl border border-slate-700 bg-slate-900/80 p-5">
              <h3 className="text-base font-semibold text-white">
                How does “Removed” impact % computation?
              </h3>
              <p className="mt-3 text-sm text-gray-300">
                Work items marked “Removed” are excluded from percentage computation.
              </p>
            </div>
          </div>
        </section>
      </div>
      <FooterSection />
    </div>
  );
};

export default HelpCenter;